import { gql, useLazyQuery, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ConsolePlanDetailFragmentDoc,
  ConsoleProductDetailFragmentDoc,
  ConsoleUserDetailQuery,
  ConsoleUserDetailQueryVariables,
  ConsoleUserDetailUserFragmentDoc,
  FetchConsoleUserDetailQuery,
  FetchConsoleUserDetailQueryVariables,
  MemberBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { getProductByPlanId } from '@/components/pages/ConsoleMembershipEdit/viewModel'
import { useRouter } from '@/core'
import { useCurrentUser } from '@/core/auth'

export type State = ReturnType<typeof useViewModel>['state']
export type Action = ReturnType<typeof useViewModel>['actions']

export const useViewModel = () => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ userId: string }>()

  const [fetchUserData, { data: fetchData }] = useLazyQuery<
    FetchConsoleUserDetailQuery,
    FetchConsoleUserDetailQueryVariables
  >(FETCH_USER, {
    variables: { userId: router.query.userId },
  })

  const { data, loading } = useQuery<
    ConsoleUserDetailQuery,
    ConsoleUserDetailQueryVariables
  >(QUERY, {
    variables: { userId: router.query.userId },
    async onCompleted({ artistCustomer }) {
      if (!artistCustomer?.user) {
        await fetchUserData()
      }
    },
  })

  const product = useMemo(() => {
    if (!data?.artistCustomer?.patron?.subscription?.planId) return null
    return getProductByPlanId(
      data?.artist,
      data?.artistCustomer?.patron?.subscription?.planId
    )
  }, [data?.artist, data?.artistCustomer?.patron?.subscription?.planId])

  return {
    state: {
      loading,
      artistId: user?.uid ?? '',
      enableFavoriteMembers:
        data?.artist?.settings?.features?.subscription?.enableFavoriteMembers ??
        false,
      product,
      membership: data?.artistCustomer?.patron ?? null,
      user: data?.artistCustomer?.user ?? fetchData?.user ?? null,
      orderCount: data?.artistCustomer?.purchaseCount ?? 0,
      unusedTicketCount: data?.artistCustomer?.ticketUnusedCount ?? 0,
      isFollower: !!data?.artistCustomer?.follower,
    },
    actions: {},
  }
}

const QUERY = gql`
  query ConsoleUserDetail($userId: ID!) {
    artist {
      id
      settings {
        features {
          subscription {
            enableFavoriteMembers
          }
        }
      }
      subscription {
        productId
        plans {
          ...ConsolePlanDetail
        }
        products {
          ...ConsoleProductDetail
        }
      }
    }
    artistCustomer(userId: $userId) {
      id
      purchaseCount
      ticketUnusedCount
      follower {
        id
      }
      patron {
        ...MemberBox
      }
      user {
        ...ConsoleUserDetailUser
      }
    }
  }
  ${ConsolePlanDetailFragmentDoc}
  ${ConsoleProductDetailFragmentDoc}
  ${MemberBoxFragmentDoc}
  ${ConsoleUserDetailUserFragmentDoc}
`

const FETCH_USER = gql`
  query FetchConsoleUserDetail($userId: ID!) {
    user(id: $userId) {
      ...ConsoleUserDetailUser
    }
  }
  ${ConsoleUserDetailUserFragmentDoc}
`
